import type { Inputs } from './roiCalculations'

// Retention presets shared by the ROI calculator and the proposal's Tailor
// panel. "Low" matches the default retention curve and is selected on load;
// "On Target" is the stretch curve. Either can be layered with +5%, and
// typing a custom retention value clears the selection.
export type Retention = {
  pctLaunched: number
  pct30: number
  pct90: number
  pct1y: number
}
export type RetentionSnapshot = Retention & {
  salesFirst90Total: number
  salesVetMonthly: number
}
export type BasePreset = 'low' | 'onTarget' | null

export const LOW_RETENTION: Retention = {
  pctLaunched: 70,
  pct30: 40,
  pct90: 40,
  pct1y: 40,
}
export const ON_TARGET_RETENTION: Retention = {
  pctLaunched: 80,
  pct30: 40,
  pct90: 80,
  pct1y: 40,
}

export interface RetentionPresetState {
  basePreset: BasePreset
  plusFive: boolean
  preBaseValues: RetentionSnapshot | null
  prePlusFiveValues: RetentionSnapshot | null
  /** Retention values the current selection applied; null when custom. */
  appliedRetention: Retention | null
}

export const INITIAL_PRESET_STATE: RetentionPresetState = {
  basePreset: 'low',
  plusFive: false,
  preBaseValues: null,
  prePlusFiveValues: null,
  appliedRetention: LOW_RETENTION,
}

export const retentionOf = (values: Retention): Retention => ({
  pctLaunched: values.pctLaunched,
  pct30: values.pct30,
  pct90: values.pct90,
  pct1y: values.pct1y,
})

export const sameRetention = (a: Retention, b: Retention) =>
  a.pctLaunched === b.pctLaunched &&
  a.pct30 === b.pct30 &&
  a.pct90 === b.pct90 &&
  a.pct1y === b.pct1y

const snapshotOf = (inputs: Inputs): RetentionSnapshot => ({
  pctLaunched: inputs.pctLaunched as number,
  pct30: inputs.pct30 as number,
  pct90: inputs.pct90 as number,
  pct1y: inputs.pct1y as number,
  salesFirst90Total: inputs.salesFirst90Total as number,
  salesVetMonthly: inputs.salesVetMonthly as number,
})

interface PresetResult {
  inputs: Inputs
  state: RetentionPresetState
}

/** Low and On Target are mutually exclusive. Selecting one first unwinds
 *  whatever the current preset (and any +5% layer) changed; selecting the
 *  active one again returns to the values it replaced. */
export function applyBasePreset(
  inputs: Inputs,
  state: RetentionPresetState,
  preset: Exclude<BasePreset, null>,
): PresetResult {
  let restored = inputs
  if (state.plusFive && state.prePlusFiveValues) {
    restored = { ...restored, ...state.prePlusFiveValues }
  }
  if (state.basePreset && state.preBaseValues) {
    restored = { ...restored, ...state.preBaseValues }
  }
  if (state.basePreset === preset) {
    return {
      inputs: restored,
      state: {
        basePreset: null,
        plusFive: false,
        preBaseValues: null,
        prePlusFiveValues: null,
        appliedRetention: null,
      },
    }
  }
  // Presets only move retention — sales per rep is left as entered.
  const retention = preset === 'low' ? LOW_RETENTION : ON_TARGET_RETENTION
  return {
    inputs: { ...restored, ...retention },
    state: {
      basePreset: preset,
      plusFive: false,
      preBaseValues: snapshotOf(restored),
      prePlusFiveValues: null,
      appliedRetention: retention,
    },
  }
}

/** +5% lifts every retention rate by five points; toggling it off restores
 *  the values it replaced. */
export function togglePlusFive(
  inputs: Inputs,
  state: RetentionPresetState,
): PresetResult {
  if (!state.plusFive) {
    const boosted: Retention = {
      pctLaunched: Math.min(100, (inputs.pctLaunched as number) + 5),
      pct30: Math.min(100, (inputs.pct30 as number) + 5),
      pct90: Math.min(100, (inputs.pct90 as number) + 5),
      pct1y: Math.min(100, (inputs.pct1y as number) + 5),
    }
    return {
      inputs: { ...inputs, ...boosted },
      state: {
        ...state,
        plusFive: true,
        prePlusFiveValues: snapshotOf(inputs),
        appliedRetention: boosted,
      },
    }
  }
  const pre = state.prePlusFiveValues
  return {
    inputs: pre ? { ...inputs, ...pre } : inputs,
    state: {
      ...state,
      plusFive: false,
      prePlusFiveValues: null,
      appliedRetention: pre
        ? state.basePreset
          ? retentionOf(pre)
          : null
        : state.appliedRetention,
    },
  }
}

/** Clears the selection when retention no longer matches what it applied
 *  (a custom value was typed). Returns the same state object if unchanged. */
export function reconcilePresets(
  inputs: Inputs,
  state: RetentionPresetState,
): RetentionPresetState {
  if (!state.appliedRetention) return state
  if (sameRetention(retentionOf(inputs as Retention), state.appliedRetention)) {
    return state
  }
  return {
    basePreset: null,
    plusFive: false,
    preBaseValues: null,
    prePlusFiveValues: null,
    appliedRetention: null,
  }
}
